/**
 * Slack/Teams-style link unfurls for chat messages.
 *
 * Each URL is fetched once through GET /api/messaging/messages/link-preview (the server
 * does the fetching, so the viewer's browser never contacts the target site
 * until they click) and cached per URL by react-query. Nothing renders while
 * loading, on error, or when the server says the preview is unavailable — a
 * missing card is better than a broken one.
 *
 * Google Drive / Docs, OneDrive and SharePoint URLs skip the unfurl entirely:
 * those files sit behind the provider's sign-in, so the server fetch would only
 * ever see a login page. They render a provider card (CloudFileCard) built from
 * the URL instead.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { messagingApi, LinkPreview } from '@/services/messagingApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { extractLinkUrls, toSafeHref } from './linkify';
import { parseCloudLink } from './cloud/cloudLinks';
import { CloudFileCard } from './cloud/CloudFileCard';

const SIX_HOURS = 6 * 60 * 60 * 1000;
const ONE_DAY = 24 * 60 * 60 * 1000;

export function useLinkPreview(url: string | null | undefined) {
  return useQuery<LinkPreview>({
    queryKey: ['link-preview', url],
    // A failed unfurl is cached as "unavailable" rather than surfaced as an
    // error: there is nothing for the reader to do about it, and it stops every
    // render of the message from re-requesting a dead link.
    queryFn: async () => {
      try {
        return await messagingApi.getLinkPreview(url as string);
      } catch {
        return {
          url: url as string,
          final_url: null,
          available: false,
          title: null,
          description: null,
          site_name: null,
          image_url: null,
          favicon_url: null,
        };
      }
    },
    enabled: !!url,
    staleTime: SIX_HOURS,
    gcTime: ONE_DAY,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

function hostnameOf(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return href;
  }
}

interface LinkPreviewCardProps {
  url: string;
  /** When set, a dismiss (×) control is shown — used by the composer. */
  onDismiss?: () => void;
  className?: string;
}

export function LinkPreviewCard(props: LinkPreviewCardProps) {
  const cloud = parseCloudLink(props.url);
  if (cloud) {
    return (
      <CloudFileCard
        provider={cloud.provider}
        kind={cloud.kind}
        name={cloud.name}
        href={cloud.href}
        onDismiss={props.onDismiss}
        className={props.className}
        testId="cloud-link-card"
      />
    );
  }
  return <GenericLinkPreviewCard {...props} />;
}

function GenericLinkPreviewCard({ url, onDismiss, className }: LinkPreviewCardProps) {
  const { t } = useAppTranslation();
  const { data } = useLinkPreview(url);

  if (!data || !data.available) return null;
  const title = data.title?.trim() || '';
  const description = data.description?.trim() || '';
  const imageUrl = toSafeHref(data.image_url);
  if (!title && !description && !imageUrl) return null;

  const href = toSafeHref(url) || toSafeHref(data.final_url);
  if (!href) return null;
  const faviconUrl = toSafeHref(data.favicon_url);
  const siteName = data.site_name?.trim() || hostnameOf(data.final_url || href);

  return (
    <div
      data-testid="link-preview-card"
      className={cn(
        'relative mt-2 flex max-w-md gap-3 overflow-hidden rounded-md border border-l-4 border-slate-200 border-l-blue-500',
        'bg-white p-2.5 text-left text-slate-900 shadow-sm dark:border-slate-700 dark:border-l-blue-400 dark:bg-slate-900 dark:text-slate-100',
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
          {faviconUrl && (
            <img
              src={faviconUrl}
              alt=""
              className="h-3.5 w-3.5 flex-shrink-0 rounded-sm"
              referrerPolicy="no-referrer"
              loading="lazy"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <span className="truncate">{siteName}</span>
        </div>
        {title && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="mt-0.5 block font-semibold leading-snug text-blue-700 hover:underline dark:text-blue-300 line-clamp-2"
            onClick={(e) => e.stopPropagation()}
          >
            {title}
          </a>
        )}
        {description && (
          <p className="mt-0.5 text-xs leading-snug text-slate-600 dark:text-slate-300 line-clamp-3">
            {description}
          </p>
        )}
      </div>
      {imageUrl && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="flex-shrink-0 self-start"
          onClick={(e) => e.stopPropagation()}
          tabIndex={-1}
        >
          <img
            src={imageUrl}
            alt=""
            className="h-16 w-16 rounded object-cover sm:h-20 sm:w-20"
            referrerPolicy="no-referrer"
            loading="lazy"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        </a>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-1 top-1 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
          title={t('messaging.linkPreview.dismiss', 'Remove preview')}
          aria-label={t('messaging.linkPreview.dismiss', 'Remove preview')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

interface MessageLinkPreviewsProps {
  content: string | null | undefined;
  metadata?: Record<string, any> | null;
  /** The sender dismissed the preview (server field suppress_link_previews). */
  suppressed?: boolean;
  /** Max number of unique URLs to unfurl. */
  limit?: number;
}

/** Up to `limit` preview cards for the links in a message. */
export function MessageLinkPreviews({ content, metadata, suppressed, limit = 3 }: MessageLinkPreviewsProps) {
  if (suppressed || metadata?.suppress_link_previews) return null;
  const urls = extractLinkUrls(content, limit);
  if (urls.length === 0) return null;
  return (
    <>
      {urls.map((url) => (
        <LinkPreviewCard key={url} url={url} />
      ))}
    </>
  );
}

export default LinkPreviewCard;
